import { onMounted, onUnmounted, ref, watch } from 'vue';
import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { useRouter } from 'vue-router';

import { NOTIFICATION_CONFIG } from '@/constants';
import { hasFunctionalConsent } from '@/helper/cookie-consent/application';
import matomoHelper from '@/helper/matomo';
import { useAuthStore } from '@/stores/auth';
import type {
  PushNotificationConfig,
  PushNotificationOnTapData,
  PushNotificationTopic,
} from '@/types/push-notifications';

/** All FCM topics used by the app (for consent withdrawal). */
const ALL_FCM_TOPICS: PushNotificationTopic[] = [
  'debugIOS',
  'public',
  'publicOnly',
  'private',
  'debug',
  'debugSunzinet',
];

export function usePushNotifications(config?: PushNotificationConfig) {
  const router = useRouter();
  const authStore = useAuthStore();
  const platform = ref(Capacitor.getPlatform());
  const isInitialized = ref(false);
  const notificationListenersAttached = ref(false);

  // Default debug whitelists
  const defaultDebugWhitelistInternal = [
    'dev.dev@uk-koeln.de',
    'stephan.schmitz@sunzinet.com',
    'lars.hartmann@sunzinet.com',
    'tobias.rademann@sunzinet.com',
    'michal.traskowski@sunzinet.com',
    'test.ukk@sunzinet.com',
  ];

  const defaultDebugWhitelist = [
    ...defaultDebugWhitelistInternal,
    'melanie.krichel@uk-koeln.de',
    'petra.van-der-wielen@uk-koeln.de',
    'sarah.biermann@uk-koeln.de',
    'ekaterina.kuzina@uk-koeln.de',
    'nicolai.schmitz@uk-koeln.de',
    'zoe.heinz@uk-koeln.de',
    'jannik.esch@uk-koeln.de',
  ];

  const debugWhitelistInternal =
    config?.debugWhitelistInternal || defaultDebugWhitelistInternal;
  const debugWhitelist = config?.debugWhitelist || defaultDebugWhitelist;

  const isPlatform = (platformName: string): boolean => {
    return platform.value === platformName;
  };

  const subscribeToFcmTopic = async (
    topic: PushNotificationTopic,
  ): Promise<void> => {
    if (
      Capacitor.isPluginAvailable('PushNotifications') &&
      (isPlatform('android') || isPlatform('ios'))
    ) {
      try {
        await FCM.subscribeTo({ topic });
        localStorage.setItem('fcmSubscribed', 'true');
        console.log(`[FCM] Successfully subscribed to ${topic}`);
      } catch (error) {
        console.error(
          `[FCM] Error subscribing to ${topic}`,
          JSON.stringify(error),
        );
      }
    }
  };

  const unsubscribeFromFcmTopic = async (
    topic: PushNotificationTopic,
  ): Promise<void> => {
    if (
      Capacitor.isPluginAvailable('PushNotifications') &&
      (isPlatform('android') || isPlatform('ios'))
    ) {
      try {
        await FCM.unsubscribeFrom({ topic });
        console.log(`[FCM] Successfully unsubscribed from ${topic}`);
      } catch (error) {
        console.error(
          `[FCM] Error unsubscribing from ${topic}`,
          JSON.stringify(error),
        );
      }
    }
  };

  const initSubscriptions = async (): Promise<void> => {
    if (!hasFunctionalConsent()) {
      return;
    }

    try {
      // Do not call FCM.refreshToken() here: on Android it triggers a new token,
      // which re-fires PushNotifications 'registration' → initSubscriptions again (loop).
      const { token } = await FCM.getToken();
      if (!token) {
        console.warn('[FCM] No FCM token yet; skip topic subscription');
        return;
      }
    } catch (e) {
      console.error('[FCM] getToken failed; skip topic subscription', e);
      return;
    }

    // Always subscribe to public topics
    await subscribeToFcmTopic('debugIOS');
    await subscribeToFcmTopic('public');
    await subscribeToFcmTopic('publicOnly');

    if (authStore.user !== null) {
      // User is logged in - subscribe to private topics
      await subscribeToFcmTopic('private');
      await unsubscribeFromFcmTopic('publicOnly');

      // Handle debug subscriptions based on user email
      const userEmail = authStore.user?.email.toLocaleLowerCase() || '';

      if (userEmail && debugWhitelist.includes(userEmail)) {
        await subscribeToFcmTopic('debug');
      }

      if (userEmail && debugWhitelistInternal.includes(userEmail)) {
        await subscribeToFcmTopic('debugSunzinet');
      } else {
        await unsubscribeFromFcmTopic('debugSunzinet');
      }
    } else {
      // FCM topic subscriptions live on the device token and survive logout and
      // session expiry. On a not-logged-in start the reactive login→logout watcher
      // never fires, so orphaned private topics from a previous login would keep
      // delivering internal/debug pushes. Reconcile by removing them here.
      await unsubscribeFromFcmTopic('private');
      await unsubscribeFromFcmTopic('debug');
      await unsubscribeFromFcmTopic('debugSunzinet');
    }
  };

  const setupNotificationListeners = (): void => {
    if (notificationListenersAttached.value) {
      return;
    }
    notificationListenersAttached.value = true;

    // Registration success
    PushNotifications.addListener('registration', (token: Token) => {
      console.log('Push registration success. Token: ' + token.value);
      void initSubscriptions();
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ' + JSON.stringify(error));
    });

    // Notification received while app is open
    PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        // {"id":"0:1762761010703297%755faaa4755faaa4","data":{"id":"42290","type":"news"},"title":"Test Push App 2.0","body":"News Detail Push from Firebase Admin SDK"}
        console.log(
          'Push Notification Received: ' + JSON.stringify(notification),
        );
        await new Promise((resolve) =>
          setTimeout(resolve, NOTIFICATION_CONFIG.DEBOUNCE_DELAY_MS),
        );
        await matomoHelper.trackEvent({
          e_c: `Push Notification`,
          e_a: 'Received',
          e_n: `${notification.id}${notification.title ? ' - ' + notification.title : ''}`,
          e_v: 1,
        });
      },
    );

    // Notification tapped
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (notification: ActionPerformed) => {
        // {"actionId":"tap","notification":{"id":"0:1762759963810947%755faaa4755faaa4","data":{"google.delivered_priority":"normal","google.original_priority":"normal","id":"42290","from":"/topics/debugSunzinet","type":"news","collapse_key":"com.sunzinet.ukkaktuell"}}}
        // {"actionId":"tap","notification":{"id":"0:1780487199381403%755faaa4755faaa4","data":{"google.delivered_priority":"normal","google.sent_time":1780487199365,"google.ttl":2419200,"google.original_priority":"normal","google.product_id":111881503,"id":"51217","from":"/topics/debugSunzinet","type":"hint","gcm.n.analytics_data":"Bundle[mParcelledData.dataSize=116]","collapse_key":"com.sunzinet.ukkaktuell"}}}
        console.log(
          'Push Notification Action performed: ' + JSON.stringify(notification),
        );
        const data = notification.notification
          .data as PushNotificationOnTapData;
        const link = `/${data.type}/${data.id}`;

        if (link.length === 0) {
          return;
        }

        // wait for the app to be ready in order to be able to track the event
        await router.isReady();
        await new Promise((resolve) =>
          setTimeout(resolve, NOTIFICATION_CONFIG.DEBOUNCE_DELAY_MS),
        );

        await matomoHelper.trackEvent({
          e_c: `Push Notification`,
          e_a: 'Click',
          e_n: `${notification.notification.id} - ${link}`,
          e_v: 1,
        });
        router.push(link);
      },
    );
  };

  const requestPermissions = async (): Promise<boolean> => {
    try {
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.error('Error on request permissions for PushNotifications');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error requesting push notification permissions:', error);
      return false;
    }
  };

  const unsubscribeAllFcmTopics = async (): Promise<void> => {
    for (const topic of ALL_FCM_TOPICS) {
      await unsubscribeFromFcmTopic(topic);
    }
    localStorage.removeItem('fcmSubscribed');
  };

  const disablePushAfterConsentRevoke = async (): Promise<void> => {
    if (
      !(isPlatform('android') || isPlatform('ios')) ||
      !Capacitor.isPluginAvailable('PushNotifications')
    ) {
      return;
    }

    try {
      await unsubscribeAllFcmTopics();
    } catch (error) {
      console.error('[FCM] Error unsubscribing all topics', error);
    }

    try {
      await FCM.setAutoInit({ enabled: false });
    } catch (error) {
      console.error('[FCM] setAutoInit(false) after consent revoke', error);
    }

    // deleteInstance() breaks subsequent topic subscribe on Android (FirebaseMessaging
    // INVALID_PARAMETERS) when consent is granted again in the same session. Topics are
    // already unsubscribed above; iOS still deletes the instance for a cleaner reset.
    if (isPlatform('ios')) {
      try {
        await FCM.deleteInstance();
      } catch (error) {
        console.error('[FCM] deleteInstance after consent revoke', error);
      }
    }

    try {
      await PushNotifications.removeAllListeners();
    } catch (error) {
      console.error('[PushNotifications] removeAllListeners', error);
    }

    notificationListenersAttached.value = false;
    isInitialized.value = false;
  };

  const initializePushNotifications = async (): Promise<void> => {
    const isPushNotificationsAvailable =
      Capacitor.isPluginAvailable('PushNotifications');

    if (
      !(isPlatform('android') || isPlatform('ios')) ||
      !isPushNotificationsAvailable
    ) {
      console.log('Push notifications not available on this platform');
      return;
    }

    if (!hasFunctionalConsent()) {
      if (isInitialized.value) {
        await disablePushAfterConsentRevoke();
      }
      return;
    }

    if (isInitialized.value) {
      console.log('Push notifications already initialized');
      return;
    }

    try {
      await FCM.setAutoInit({ enabled: true });

      const hasPermission = await requestPermissions();

      if (hasPermission) {
        // Listeners must be registered before register() so the registration event is not missed.
        setupNotificationListeners();
        await PushNotifications.register();
      }

      const autoInitStatus = await FCM.isAutoInitEnabled();
      console.log(
        'Auto init is ' + (autoInitStatus.enabled ? 'enabled' : 'disabled'),
      );

      isInitialized.value = true;
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  };

  const cleanupSubscriptions = async (): Promise<void> => {
    if (!hasFunctionalConsent()) {
      return;
    }
    try {
      await unsubscribeFromFcmTopic('private');
      await unsubscribeFromFcmTopic('debug');
      await unsubscribeFromFcmTopic('debugSunzinet');
      localStorage.removeItem('fcmSubscribed');
    } catch (error) {
      console.error(
        'Error cleaning up push notification subscriptions:',
        error,
      );
    }
  };

  // Watch for auth state changes to update subscriptions
  watch(
    () => authStore.user,
    async (newUser, oldUser) => {
      if (!isInitialized.value || !hasFunctionalConsent()) {
        return;
      }

      if (oldUser === null && newUser !== null) {
        // User logged in
        await initSubscriptions();
        return;
      }

      if (oldUser !== null && newUser === null) {
        // User logged out
        await cleanupSubscriptions();
      }
    },
  );

  const syncPushWithCookieConsent = async (): Promise<void> => {
    if (
      !(isPlatform('android') || isPlatform('ios')) ||
      !Capacitor.isPluginAvailable('PushNotifications')
    ) {
      return;
    }

    if (hasFunctionalConsent()) {
      await initializePushNotifications();
    } else {
      await disablePushAfterConsentRevoke();
    }
  };

  const onConsentChanged = () => {
    void syncPushWithCookieConsent();
  };

  onMounted(() => {
    window.addEventListener('consentChanged', onConsentChanged);
  });

  onUnmounted(() => {
    window.removeEventListener('consentChanged', onConsentChanged);
  });

  return {
    platform,
    isInitialized,
    initializePushNotifications,
    initSubscriptions,
    cleanupSubscriptions,
    subscribeToFcmTopic,
    unsubscribeFromFcmTopic,
    isPlatform,
    syncPushWithCookieConsent,
  };
}
